/**
 * PEG-546: Claims-management API client (admin-session only).
 *
 * All three endpoints live under /v1/retailers/:retailerId/claims and
 * are gated server-side by the existing PEG-509 admin-session middleware
 * + the controller's defense-in-depth requireAdminContext check. The
 * client does not enforce admin-mode; it lets the API 401 when called
 * from a retailer session, which keeps the gating in one place
 * (server side).
 */
import { $fetch } from '@/boot/fetch'
import { replacePathTokens } from '@/utils/routes.util'
import { validateQueryParams } from '@/utils/validator.util'

const BASE = '/retailers/{{retailerId}}/claims'

export default {
  /**
   * @description List all claims for a retailer with optional filters.
   * @param {String} retailerId
   * @param {Object} params - { statuses?, claimantTypes? } (comma-separated)
   */
  async list(retailerId, params = {}) {
    const url = replacePathTokens(BASE, { retailerId })
    const cleaned = validateQueryParams(params, ['statuses', 'claimantTypes'])
    return $fetch.peg(url, { params: cleaned })
  },

  /**
   * @description Get a single claim with full context (claim + verification +
   *               transitions audit-trail + returner-history placeholder).
   */
  async getById(retailerId, claimId) {
    const url = replacePathTokens(`${BASE}/{{claimId}}`, { retailerId, claimId })
    return $fetch.peg(url)
  },

  /**
   * @description Transition a claim's status. The server enforces the linear
   *               state machine (SUBMITTED → UNDER_REVIEW → VALIDATED/DENIED →
   *               RESOLVED) and the resolutionNotes-required invariant for
   *               VALIDATED/DENIED. Returns 409 on illegal/concurrent
   *               transitions; FE refetches + retries.
   * @param {String} retailerId
   * @param {String} claimId
   * @param {Object} body - { targetStatus, resolutionNotes? }
   */
  async transition(retailerId, claimId, body) {
    const url = replacePathTokens(`${BASE}/{{claimId}}/transition`, {
      retailerId,
      claimId,
    })
    return $fetch.peg(url, {
      method: 'PUT',
      body,
    })
  },
}
